import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ShoppingCart, Archive, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddListDialog from './AddListDialog';
import ShoppingListView from './ShoppingListView';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  progressBar: '#4ADE80',
  progressBg: '#D1FAE5',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const ActiveListsTab = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await API.get('/shopping/lists?status=active');
      setLists(response.data);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
      toast.error('Failed to load shopping lists');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id, e) => {
    e.stopPropagation();
    try {
      await API.put(`/shopping/lists/${id}`, { status: 'archived' });
      toast.success('List archived');
      fetchLists();
    } catch (error) {
      toast.error('Failed to archive list');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this list? This cannot be undone.')) return;
    try {
      await API.delete(`/shopping/lists/${id}`);
      toast.success('List removed');
      fetchLists();
    } catch (error) {
      toast.error('Failed to remove list');
    }
  };

  const getProgress = (list) => {
    if (list.item_count === 0) return 0;
    return Math.round((list.purchased_count / list.item_count) * 100);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  // If a list is selected, show its items
  if (selectedList) {
    return (
      <ShoppingListView 
        list={selectedList} 
        onBack={() => {
          setSelectedList(null);
          fetchLists();
        }}
        onUpdate={fetchLists}
      />
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h3 
            className="text-xl font-semibold"
            style={{ color: THEME.dark }}
          >
            Shopping Lists
          </h3>
          <p className="text-sm text-gray-500 mt-1">Manage your shopping lists</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.primary,
            color: THEME.dark,
          }}
          data-testid="add-list-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New List
        </Button>
      </div>

      {lists.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.lighter,
            borderColor: THEME.border,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <ShoppingCart className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Ready to Shop Smarter?</h4>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Create your first shopping list and never forget an item again
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="shadow-sm hover:shadow-md transition-shadow"
            style={{ 
              backgroundColor: THEME.primary,
              color: THEME.dark,
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Your First List
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {lists.map((list) => {
            const progress = getProgress(list);
            return (
              <Card 
                key={list.id} 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 overflow-hidden group"
                style={{ 
                  borderColor: THEME.border,
                  borderWidth: '1px',
                  borderLeftWidth: '4px',
                  borderLeftColor: THEME.primary,
                  backgroundColor: '#FFFFFF',
                }}
                onClick={() => setSelectedList(list)}
                data-testid={`list-card-${list.id}`}
              >
                <CardHeader className="pb-3 pt-5 px-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-900">
                        <span className="truncate">{list.name}</span>
                        <ChevronRight 
                          className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-green-600 transition-colors" 
                        />
                      </CardTitle>
                      {list.description && (
                        <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{list.description}</p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="space-y-4">
                    {/* Progress section */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {list.purchased_count} / {list.item_count} items
                      </span>
                      <Badge 
                        className="text-xs font-medium px-2.5 py-0.5"
                        style={{ 
                          backgroundColor: progress === 100 ? THEME.light : '#F3F4F6',
                          color: progress === 100 ? THEME.dark : '#6B7280',
                        }}
                      >
                        {progress}%
                      </Badge>
                    </div>
                    
                    {/* Custom progress bar */}
                    <div 
                      className="h-2.5 rounded-full overflow-hidden"
                      style={{ backgroundColor: THEME.progressBg }}
                    >
                      <div 
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{ 
                          width: `${progress}%`,
                          backgroundColor: THEME.progressBar,
                        }}
                      />
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleArchive(list.id, e)}
                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        data-testid={`archive-list-${list.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDelete(list.id, e)}
                        className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                        data-testid={`delete-list-${list.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddListDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchLists();
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default ActiveListsTab;
